/* AddEditConnectionComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

const Renderer = require("./AddEditConnectionRenderer");

/**
 * @class AddEditConnectionComponent
 * @extends EventEmitter
 *
 * @fires AddEditConnectionComponent#Data - Emitted when connection data is updated.
 * @fires AddEditConnectionComponent#Shutdown - Emitted when the component is shutting down.
 * @fires AddEditConnectionComponent#DirtyStateChanged - Emitted when the connection's dirty state changes.
 *
 * @listens ConnectionEntity#event:DirtyStateChanged - Listens for changes in connection entity state.
 * @listens ManageConnectionsComponent#event:OrganizationChanged - Listens for organization changes.
 */
class AddEditConnectionComponent extends EventEmitter {
    /**
     * Creates an instance of AddEditConnectionComponent.
     *
     * @param {Object} data - Configuration data for initializing the component.
     */
    constructor(data) {
        super();
        this._data = data;
        this._connectionEntityDef = this._data.entityDef;
        this._activeOrgId = this._data.activeOrgId;
        this._isNewConnection = !this._data.connectionEntity;
        this._uiUtils = this._data.uiUtils;
        this._connectionEntity = _.get(
            this._data,
            "connectionEntity",
            new this._connectionEntityDef(this._activeOrgId)
        );
        this._typeEntities = this._data.typeEntities;
        this._advancedConfig = {};

        if (!this._typeEntities || this._typeEntities.length <= 0) {
            this.rejectPopUp("No connection types available");
        }
        this._typeDataMap = this.getTypeDataMap();
        this._typeOptions = this.getTypeOpts();
        this._typeOptJtypeMap = this.getTypeOptJtypeMap();

        // if (_.get(this._data, "manageRoleComponent._canEdit", false)) {
        this._isEditable = true;
        // }
        this._isInherited = this._data.isInherited || false;
        this._isSystemManaged = this._data.isSystemManaged || false;

        this._userFeedback = this._data.userFeedback;
        this._manageConnectionComponent = this._data.manageConnectionComponent;
        this._registerListeners();
        this._renderer = new Renderer(this);
    }

    get renderer() {
        return this.getRenderer();
    }

    get rootElement() {
        return this._renderer.getRootElement();
    }

    get activeOrgId() {
        return this._activeOrgId;
    }

    get userFeedback() {
        return this._userFeedback;
    }

    // prettier-ignore
    set "$teve.ui.context"(context) { //jshint ignore:line
        this._context = context;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _triggerData(data) {
        /**
         * @event AddEditConnectionComponent#Data
         * @param {Object} - entity properties.
         */
        this.emit("Data", data);
    }

    _registerListeners() {
        this._connectionEntity.on("DirtyStateChanged", this.dirtyStateChanged.bind(this));
        this._manageConnectionComponent.on(
            "OrganizationChanged",
            this._onOrganizationChanged.bind(this)
        );
    }

    _unregisterListeners() {
        this._connectionEntity.removeListener("DirtyStateChanged", this.dirtyStateChanged);
        this._manageConnectionComponent.removeListener(
            "OrganizationChanged",
            this._onOrganizationChanged
        );
    }

    _shutdown() {
        if (_.isUndefined(this._renderer)) {
            return Promise.resolve();
        }

        this._unregisterListeners();

        return this._renderer
            .shutdown()
            .then(() => {
                [
                    "_renderer",
                    "_isSeeded",
                    "_connectionEntityDef",
                    "_connectionEntity",
                    "_activeOrgId",
                    "_manageConnectionComponent",
                    "_shutdownProm",
                    "_data",
                    "_userFeedback",
                    "_isInherited",
                    "_isSystemManaged",
                    "_isNewConnection",
                    "_typeEntities",
                    "_typeDataMap",
                    "_typeOptions",
                    "_typeOptJtypeMap",
                    "_isEditable",
                    "_advancedConfig",
                ].forEach((prop) => (this[prop] = undefined));
                this.removeAllListeners();
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    async saveConnection() {
        if (this._isNewConnection) {
            return this._connectionEntity.create();
        }

        if (_.invoke(this._connectionEntity, "isDirty")) {
            return this._connectionEntity.update();
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    getTypeDataMap() {
        if (!this._typeEntities) {
            return {};
        }
        return this._typeEntities.reduce((acc, ety) => {
            acc[ety.id] = {
                values:
                    ety.id === this._connectionEntity.connectionTypeId
                        ? this._connectionEntity.connectionInformation
                        : {},
            };
            return acc;
        }, {});
    }

    getTypeOpts() {
        if (!this._typeEntities) {
            return [];
        }

        return this._typeEntities.map((ety) => ({
            label:
                ety.displayName ||
                (ety.properties &&
                    ety.properties.metadata &&
                    ety.properties.metadata.displayName) ||
                ety.name,
            value: ety.id,
        }));
    }

    getTypeOptJtypeMap() {
        if (!this._typeEntities) {
            return {};
        }
        return this._typeEntities.reduce((map, ety) => {
            map[ety.id] = ety.jtype;
            return map;
        }, {});
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    closePopUp() {
        this.shutdown().then(() => {
            this.rejectPopUp({userRejected: true});
        });
    }

    resolvePopup(data) {
        if (this._context && typeof this._context.resolve === "function") {
            this._context.resolve(data);
        } else {
            console.warn("resolvePopup: _context is undefined or invalid.");
        }
    }

    rejectPopUp(data) {
        if (this._context && typeof this._context.reject === "function") {
            this._context.reject(data);
        } else {
            console.warn("rejectPopUp: _context is undefined or invalid.");
        }
    }
    testConnection(data) {
        const workInput = {
            serviceConnection: {
                connectionTypeId: data.connectionTypeId,
                connectionInformation: data.connectionInfo,
            },
        };
        // Active org, not the connection's owning org: an inherited connection's tenant is an
        // ancestor the caller may not be authorized to launch work against.
        const workTarget = {
            category: "entity",
            entityType: "ServiceConnection",
            method: "single",
            id: this._connectionEntity.id,
        };
        return this._connectionEntityDef.runTestConnectionWork(
            this._activeOrgId,
            workTarget,
            workInput
        );
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    dirtyStateChanged() {
        const dirtyState = this._connectionEntity.isDirty();
        this.emit("DirtyStateChanged", dirtyState);
    }

    _onOrganizationChanged(orgObj) {
        if (this.activeOrgId === orgObj.id) {
            return;
        }
        this._activeOrgId = orgObj.id;

        this.closePopUp();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = AddEditConnectionComponent;
